"use strict";
import { isArray, isReference } from "@apollo/client/utilities";
import { call, delay, put } from "typed-redux-saga";
import { getNativeAddress } from "constants/uniswapAddresses";
import {
  PortfolioBalancesDocument
} from "data/graphql/uniswap-data-api/__generated__/types-and-hooks";
import { fromGraphQLChain } from "features/chains/utils";
import { GQL_QUERIES_TO_REFETCH_ON_TXN_UPDATE } from "features/portfolio/portfolioUpdates/constants";
import { fetchOnChainBalances } from "features/portfolio/portfolioUpdates/fetchOnChainBalances";
import { getCurrenciesWithExpectedUpdates } from "features/portfolio/portfolioUpdates/getCurrenciesWithExpectedUpdates";
import { createTokenBalanceRef } from "features/portfolio/portfolioUpdates/getInstantTokenBalanceUpdateApolloLink";
import { addTokensToBalanceOverride } from "features/portfolio/slice/slice";
import { getEnabledChainIdsSaga } from "features/settings/saga";
import { buildCurrencyId } from "utils/uniswapCurrencyId";
import { logger } from "utilities/src/logger/logger";
import { ONE_SECOND_MS } from "utilities/src/time/time";
const REFETCH_DELAY = ONE_SECOND_MS * 3;
export function* refetchGQLQueriesViaOnchainOverrideVariant({
  transaction,
  apolloClient,
  activeAddress
}) {
  const owner = transaction.from;
  const currenciesWithBalanceToUpdate = getCurrenciesWithExpectedUpdates(transaction);
  if (owner !== activeAddress) {
    return;
  }
  if (currenciesWithBalanceToUpdate) {
    yield* call(modifyLocalCache, {
      apolloClient,
      ownerAddress: activeAddress,
      currencyIds: currenciesWithBalanceToUpdate
    });
  }
  yield* delay(REFETCH_DELAY);
  yield* call([apolloClient, apolloClient.refetchQueries], { include: GQL_QUERIES_TO_REFETCH_ON_TXN_UPDATE });
}
function* modifyLocalCache({
  apolloClient,
  ownerAddress,
  currencyIds
}) {
  logger.debug(
    "refetchGQLQueriesViaOnchainOverrideVariantSaga.ts",
    "modifyLocalCache",
    `[ITBU] modifyLocalCache called with ${currencyIds.size} currencyIds`,
    { currencyIds }
  );
  yield* put(
    addTokensToBalanceOverride({
      ownerAddress,
      currencyIds: Array.from(currencyIds)
    })
  );
  const { gqlChains } = yield* call(getEnabledChainIdsSaga);
  const cachedPortfolio = apolloClient.readQuery({
    query: PortfolioBalancesDocument,
    variables: {
      ownerAddress,
      chains: gqlChains
    }
  })?.portfolios?.[0];
  if (!cachedPortfolio) {
    logger.warn(
      "refetchGQLQueriesViaOnchainOverrideVariantSaga.ts",
      "modifyLocalCache",
      "[ITBU] No `cachedPortfolio` found"
    );
    return;
  }
  const onchainBalancesByCurrencyId = yield* call(fetchOnChainBalances, {
    apolloCache: apolloClient.cache,
    cachedPortfolio,
    accountAddress: ownerAddress,
    currencyIds
  });
  apolloClient.cache.modify({
    id: apolloClient.cache.identify(cachedPortfolio),
    fields: {
      tokenBalances(tokenBalancesRefs, { readField }) {
        if (!isArray(tokenBalancesRefs)) {
          return tokenBalancesRefs;
        }
        tokenBalancesRefs.forEach((tokenBalanceRef) => {
          const tokenRef = readField("token", tokenBalanceRef);
          const chainId = fromGraphQLChain(readField("chain", tokenRef));
          const tokenAddress = chainId ? readField("address", tokenRef) ?? getNativeAddress(chainId) : null;
          if (!tokenRef || !chainId || !tokenAddress) {
            logger.error(new Error("Missing required value: `tokenRef`, `chainId` or `tokenAddress`"), {
              tags: {
                file: "refetchGQLQueriesViaOnchainOverrideVariantSaga.ts",
                function: "modifyLocalCache"
              },
              extra: { tokenRef, chainId, tokenAddress }
            });
            return;
          }
          const currencyId = buildCurrencyId(chainId, tokenAddress).toLowerCase();
          const onchainBalance = onchainBalancesByCurrencyId.get(currencyId);
          if (!onchainBalance) {
            return;
          }
          const onchainQuantity = onchainBalance.quantity;
          onchainBalancesByCurrencyId.delete(currencyId);
          if (onchainQuantity === void 0) {
            logger.warn(
              "refetchGQLQueriesViaOnchainOverrideVariantSaga.ts",
              "modifyLocalCache",
              "[ITBU] Unable to override local cache because of missing `onchainQuantity`",
              {
                currencyId
              }
            );
            return;
          }
          const cachedQuantity = readField("quantity", tokenBalanceRef);
          const tokenBalanceId = apolloClient.cache.identify(tokenBalanceRef);
          logger.debug(
            "refetchGQLQueriesViaOnchainOverrideVariantSaga.ts",
            "modifyLocalCache",
            `[ITBU] Calling apolloClient.cache.modify for ${currencyId}`,
            { tokenBalanceRef, tokenBalanceId }
          );
          apolloClient.cache.modify({
            id: tokenBalanceId,
            fields: {
              quantity: () => {
                return onchainQuantity;
              },
              denominatedValue: (cachedDenominatedValue) => {
                if (!cachedDenominatedValue) {
                  logger.debug(
                    "refetchGQLQueriesViaOnchainOverrideVariantSaga.ts",
                    "modifyLocalCache",
                    `[ITBU] No cachedDenominatedValue found for ${currencyId}`
                  );
                  return cachedDenominatedValue;
                }
                if (!cachedQuantity) {
                  logger.debug(
                    "refetchGQLQueriesViaOnchainOverrideVariantSaga.ts",
                    "modifyLocalCache",
                    `[ITBU] No cachedQuantity found for ${currencyId}`
                  );
                  return cachedDenominatedValue;
                }
                if (isReference(cachedDenominatedValue)) {
                  logger.error(new Error("Unexpected `cachedDenominatedValue` as Reference instead of Amount"), {
                    tags: {
                      file: "refetchGQLQueriesViaOnchainOverrideVariantSaga.ts",
                      function: "modifyLocalCache"
                    },
                    extra: {
                      currencyId,
                      cachedDenominatedValue
                    }
                  });
                  return cachedDenominatedValue;
                }
                const value = cachedDenominatedValue.value * onchainQuantity / cachedQuantity;
                logger.debug(
                  "refetchGQLQueriesViaOnchainOverrideVariantSaga.ts",
                  "modifyLocalCache",
                  `[ITBU] Overriding ${currencyId} with $${value}`
                );
                return {
                  ...cachedDenominatedValue,
                  value
                };
              }
            }
          });
        });
        const newTokenBalancesRefs = [];
        Array.from(onchainBalancesByCurrencyId).forEach(([currencyId]) => {
          const onchainBalanceQuantity = onchainBalancesByCurrencyId.get(currencyId)?.quantity;
          const denominatedValue = onchainBalancesByCurrencyId.get(currencyId)?.denominatedValue;
          if (onchainBalanceQuantity === void 0) {
            logger.warn(
              "refetchGQLQueriesViaOnchainOverrideVariantSaga.ts",
              "modifyLocalCache",
              "[ITBU] No `onchainBalance.quantity` found for token",
              { currencyId, ownerAddress }
            );
            return;
          }
          const newTokenBalanceRef = createTokenBalanceRef({
            apolloCache: apolloClient.cache,
            ownerAddress,
            currencyId,
            onchainBalanceQuantity,
            denominatedValue: denominatedValue ?? null
          });
          if (!newTokenBalanceRef) {
            return;
          }
          newTokenBalancesRefs.push(newTokenBalanceRef);
        });
        return newTokenBalancesRefs.length > 0 ? [...tokenBalancesRefs, ...newTokenBalancesRefs] : tokenBalancesRefs;
      }
    }
  });
}
